import { useCallback } from "react";
import { useToast } from "@racoon/ui";
import { Namespace, useT } from "@racoon/i18n";
import {
  useCreateContainerGroupMutation,
  useUpdateContainerGroupMutation,
} from "~/shared/api/container-group/container-group.grpc";
import type { ContainerGroupForm } from "../components/container-group.modal";

/**
 * Maps `ContainerGroupModal`'s form values onto the create/update mutations.
 * Shared by the PL modal's container-groups block and the pickup-locations
 * cascade, so the payload mapping lives in one place.
 */
export const useContainerGroupSubmit = () => {
  const [createContainerGroup] = useCreateContainerGroupMutation();
  const [updateContainerGroup] = useUpdateContainerGroupMutation();
  const t = useT();
  const { toast } = useToast();

  return useCallback(
    async ({
      formData,
      editingGroup,
      locationId,
    }: {
      formData: ContainerGroupForm;
      /**
       * The group being edited; absent means create. Only its id is read, and
       * the two call sites hold it as a Connect message and a REST row.
       */
      editingGroup?: { id: bigint | number } | null;
      locationId?: bigint;
    }) => {
      const payload = {
        ownerOrganizationId: BigInt(formData.owner_organization_id),
        contractId: formData.contract_id ? BigInt(formData.contract_id) : undefined,
        containerTypeId: BigInt(formData.container_type_id),
        numberOfContainers: formData.number_of_containers,
        pickupLocationId: locationId,
      };

      const zone = formData.service_provider_ids?.map(BigInt);

      if (editingGroup) {
        await updateContainerGroup({
          id: BigInt(editingGroup.id),
          ...payload,
          // Unset keeps the stored zone; an empty list clears it.
          ...(zone ? { serviceProviderIds: { ids: zone } } : {}),
        }).unwrap();
        toast({ title: t(Namespace.PickupLocation, "containerGroupUpdated") });
      } else {
        await createContainerGroup({
          ...payload,
          serviceProviderIds: zone ?? [],
        }).unwrap();
        toast({ title: t(Namespace.PickupLocation, "containerGroupCreated") });
      }
    },
    [createContainerGroup, updateContainerGroup, toast, t],
  );
};
